import { useCommentStore } from "../../stores/commentStore";
import { WriteRewriteTourVariations, TourSignal, PluginsQuickstartTourVariations } from "./TourTypes";
import { getWriteRewriteTour } from "./Tours/WriteRewrite/WriteRewriteTour";
import { useEffect, useState, useCallback } from "react";
import { useStickyTourStore } from "../../stores/stickyTourStore";
import InlineSVG from "react-inlinesvg";
import { StickyButton } from "./StickyButton";
import Crossroads from "./Crossroads";
import { FIND_TOUR_AGAIN_TOUR } from "./Tours/FindTourAgain";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import { SudoTooltip } from "../../sudokit/components/SudoTooltip";
import { AppEventPublisher } from "../../AppEventPublisher";
import Auth from "../../Auth";
import * as Sentry from "@sentry/react";
import { countWords } from "../../Utils";
import { SAMPLE_CHAPTER_HTML } from "./fixtures/sampleChapter";

/** On Story Bible tour completion, normalize credits to 10k for bonus variant (idempotent). */
export const completeStoryBibleTourExperiment = async () => {
  try {
    await Auth.fetchWithIdToken("/api/experiments/story-bible-tour/complete", { method: "POST" });
  } catch (error) {
    Sentry.captureMessage("Failed to complete story bible tour experiment (credit normalization)", {
      level: "warning",
      extra: { error },
    });
  }
};

export const StoryBibleTourIntroContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      In this tour we'll teach you how to use <strong>Story Bible</strong> to take you from idea to first draft.
      <StickyButton
        text="Let's go"
        variant="go-button"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
      />
    </>
  );
};

export const PluginsWriteRewriteBridgeContent = () => {
  const startTour = useStickyTourStore((state) => state.startTour);

  useEffect(() => {
    AppEventPublisher.onboarding.shownWriteRewriteNextFromPlugins();
  }, []);

  const handleShowMe = () => {
    AppEventPublisher.onboarding.acceptedWriteRewriteTourFromPlugins();
    startTour(getWriteRewriteTour(WriteRewriteTourVariations.FROM_HELP_MENU), { startAtStepId: "1.1" });
  };

  const handleMaybeLater = () => {
    AppEventPublisher.onboarding.declinedWriteRewriteTourFromPlugins();
    startTour(FIND_TOUR_AGAIN_TOUR);
  };

  return (
    <>
      <p>
        Would you like to learn how to use the <strong>Write</strong> and <strong>Rewrite</strong> Tools?
      </p>
      <Crossroads
        goText="Show me!"
        noText="Maybe later"
        goOnClick={handleShowMe}
        noOnClick={handleMaybeLater}
      />
    </>
  );
};

export const StoryBibleWelcomeContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const endTour = useStickyTourStore((state) => state.endTour);
  const startTour = useStickyTourStore((state) => state.startTour);

  const handleShowMe = () => {
    sendFlowSignal(TourSignal.STICKY_NEXT);
  };

  return (
    <>
      👋 Let's use <strong>Story Bible</strong> to take you from an idea to a first draft!
      <Crossroads
        goText="Show me"
        noText="Later"
        goOnClick={handleShowMe}
        noOnClick={() => {
          endTour(true);
          startTour(FIND_TOUR_AGAIN_TOUR);
        }}
      />
    </>
  );
};

export const NewProjectFlowStoryBibleWelcomeContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>
        Generate your first draft with <strong>Story Bible</strong>.
      </p>
      <StickyButton
        text="Show me how"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="bottom-[-20]"
      />
    </>
  );
};

export const StoryBibleSeedInputContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const seed = useStoryBibleStore((state) => state.seed);
  const isStoryBibleLoaded = useStoryBibleStore((state) => state.isStoryBibleLoaded);
  const isBraindumpEmpty = isStoryBibleLoaded && (!seed || seed.trim() === "");

  if (isBraindumpEmpty) {
    return (
      <p>
        Think of <strong>Braindump</strong> as a seed for your story. Jot down everything you know so far.
      </p>
    );
  }

  return (
    <>
      <p>
        Think of <strong>Braindump</strong> as a seed for your story. Add any missing details here.
      </p>
      <StickyButton
        text="Done"
        variant="go-button"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="bottom-[-20]"
      />
    </>
  );
};

export const StoryBibleSynopsisEditContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>
        ✨ Nice! Edit this synopsis to make it your own. Or try <strong>Rewrite</strong> at the bottom of the textbox and Sudowrite will improve it.
      </p>
      <StickyButton
        text="OK"
        variant="go-button"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="bottom-[-20]"
      />
    </>
  );
};

export const StoryBibleRewriteTipContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>
        Clicking into textboxes can expose more options, like <strong>Rewrite</strong>. Give it a try!
      </p>
      <StickyButton
        text="Got it"
        variant="go-button"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="bottom-[-20]"
      />
    </>
  );
};

export const StoryBibleAddCharacterContent = () => {
  const setToStepById = useStickyTourStore((state) => state.setToStepById);

  return (
    <>
      <p>
        You can <strong>add more characters</strong> here, or skip this step.
      </p>
      <StickyButton
        text="OK"
        variant="go-button"
        onClick={() => setToStepById("7")}
      />
    </>
  );
};

export const StoryBibleCastGeneratedContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>
        You can update these character details or ask Sudowrite to{" "}
        <SudoTooltip
          text="Remember, clicking into textboxes can expose extra options like Rewrite"
          position="top"
          triggerAs="span"
          triggerClassName="inline"
        >
          <strong className="underline decoration-dotted decoration-1 underline-offset-[6px] cursor-help">Rewrite</strong>
        </SudoTooltip>{" "}
        them.
      </p>
      <StickyButton
        text="OK"
        variant="go-button"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="bottom-[-20]"
      />
    </>
  );
};

export const StoryBibleOutlineGeneratedContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>
        Your Story is coming to life! Check out your outline and make changes if you like, or ask Sudowrite to <strong>Rewrite</strong> it.
      </p>
      <StickyButton
        text="OK"
        variant="go-button"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="bottom-[-20]"
      />
    </>
  );
};

export const StoryBibleReviewScenesContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>Take a look at your scenes and make any changes now before we write your draft.</p>
      <StickyButton
        text="OK"
        variant="go-button"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
      />
    </>
  );
};

export const StoryBibleStyleTipContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <p>
      Great! If you ever want to go back to the style menu, you can click <strong>Start Over</strong> under the
      <InlineSVG
        src="/assets/three-dots.svg"
        className="inline-block "
      />
      Menu. <button onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}>Got it!</button>
    </p>
  );
};

export const StoryBibleFinishTourContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p>
        Revise with <strong>Rewrite</strong> and <strong>Describe</strong>. And of course, the good old fashioned keyboard!
      </p>
      <StickyButton
        text="Let's do this!"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="bottom-[20] left-[80%] hover:left-[90%] transition-all duration-200"
      />
    </>
  );
};

const PASTE_WORD_THRESHOLD = 500;

const PastePromptContent = ({ insertSampleOnSkip }: { insertSampleOnSkip: boolean }) => {
  const tiptapEditor = useCommentStore((state) => state.tiptapEditor);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    if (!tiptapEditor) return;

    const updateCount = () => {
      setWordCount(countWords(tiptapEditor.state.doc.textContent));
    };
    updateCount();

    tiptapEditor.on("update", updateCount);
    return () => {
      tiptapEditor.off("update", updateCount);
    };
  }, [tiptapEditor]);

  const handleSkip = useCallback(() => {
    if (insertSampleOnSkip && tiptapEditor) {
      tiptapEditor.commands.insertContent(SAMPLE_CHAPTER_HTML);
    }
    sendFlowSignal(TourSignal.STICKY_NEXT);
  }, [insertSampleOnSkip, tiptapEditor, sendFlowSignal]);

  const meetsThreshold = wordCount >= PASTE_WORD_THRESHOLD;

  return (
    <>
      <p>Paste a snippet of your work (at least 500 words) to try out your plugins!</p>
      {meetsThreshold ? (
        <StickyButton
          text="Done"
          variant="go-button"
          size="small"
          onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        />
      ) : (
        <StickyButton
          text="Skip"
          variant="no-button"
          size="small"
          onClick={handleSkip}
        />
      )}
    </>
  );
};

export const PasteWorkStepContent = () => {
  const tiptapEditor = useCommentStore((state) => state.tiptapEditor);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const variation = useStickyTourStore((state) => state.activeTourVariation) as PluginsQuickstartTourVariations;
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const [editorEmpty, setEditorEmpty] = useState(true);
  const [checkedOnMount, setCheckedOnMount] = useState(false);

  useEffect(() => {
    setProseGeneratorActive(false);
  }, [setProseGeneratorActive]);

  useEffect(() => {
    if (!tiptapEditor) return;

    const check = () => {
      setEditorEmpty(tiptapEditor.isEmpty);
    };
    check();
    setCheckedOnMount(true);

    tiptapEditor.on("update", check);
    return () => {
      tiptapEditor.off("update", check);
    };
  }, [tiptapEditor]);

  // FROM_HELP_MENU: auto-skip if >= 500 words
  useEffect(() => {
    if (variation !== PluginsQuickstartTourVariations.FROM_HELP_MENU) return;
    if (!tiptapEditor || !checkedOnMount) return;

    const wc = countWords(tiptapEditor.state.doc.textContent);
    if (wc >= PASTE_WORD_THRESHOLD) {
      sendFlowSignal(TourSignal.STICKY_NEXT);
    }
  }, [variation, tiptapEditor, checkedOnMount, sendFlowSignal]);

  if (!tiptapEditor || !checkedOnMount) return null;

  if (variation === PluginsQuickstartTourVariations.IMPORT) {
    if (!editorEmpty) {
      return (
        <>
          <p>I pasted a snippet of your import so you can try out your plugins! Your full import is still in progress.</p>
          <StickyButton
            text="Got it"
            variant="go-button"
            size="small"
            onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
          />
        </>
      );
    }
    // Fallback: import snippet didn't land — show paste prompt
    return <PastePromptContent insertSampleOnSkip />;
  }

  if (variation === PluginsQuickstartTourVariations.FROM_HELP_MENU) {
    const wc = countWords(tiptapEditor.state.doc.textContent);
    if (wc >= PASTE_WORD_THRESHOLD) return null;
    // < 500 words: paste prompt, but Skip doesn't insert content
    return <PastePromptContent insertSampleOnSkip={false} />;
  }

  // NO_IMPORT (default)
  return <PastePromptContent insertSampleOnSkip />;
};
